import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ProjectsPanel } from "./ProjectsPanel";
import { DocumentsPanel, UpdatesPanel, InboxPanel, SettingsPanel } from "./ContentPanels";
import RequestsPanel from "./RequestsPanel";

const TABS = ["Projects", "Documents", "Requests", "Updates", "Inbox", "Settings"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("Projects");
  const [user, setUser] = useState(null);
  const [data, setData] = useState({ projects: [], documents: [], updates: [], messages: [], inquiries: [], accessRequests: [], settings: {} });
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      const [projects, documents, updates, messages, inquiries, accessRequests, settings] = await Promise.all([
        api.get("/admin/projects"), api.get("/admin/documents"), api.get("/admin/updates"),
        api.get("/admin/messages"), api.get("/admin/inquiries"), api.get("/admin/access-requests"), api.get("/settings"),
      ]);
      setData({
        projects: projects.data, documents: documents.data, updates: updates.data,
        messages: messages.data, inquiries: inquiries.data, accessRequests: accessRequests.data, settings: settings.data,
      });
    } catch {
      localStorage.removeItem("bsg_admin_token");
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("bsg_admin_token")) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).then(reload).catch(() => navigate("/admin"));
  }, [navigate, reload]);

  const logout = () => {
    localStorage.removeItem("bsg_admin_token");
    navigate("/admin");
  };

  if (!user) return <div className="min-h-screen bg-ink flex items-center justify-center text-white/40 font-mono text-xs tracking-[0.3em] uppercase">Loading…</div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-ivory">
      <header className="border-b border-white/10 px-6 lg:px-10 h-16 flex items-center justify-between">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">BSGOTECH Founder CMS</span>
          <span className="ml-4 text-white/40 text-xs">{user.email}</span>
        </div>
        <div className="flex items-center gap-6">
          <a href="/" className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-gold transition-colors">View Site</a>
          <button data-testid="admin-logout-btn" onClick={logout} className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-red-400 transition-colors">Logout</button>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        <nav className="lg:w-56 shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 p-4 lg:p-6 flex lg:flex-col gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`text-left px-4 py-3 font-mono text-[11px] tracking-[0.2em] uppercase whitespace-nowrap transition-colors ${
                tab === t ? "bg-gold/10 text-gold border border-gold/30" : "text-white/50 hover:text-ivory border border-transparent"
              }`}
            >
              {t}
              {t === "Inbox" && data.messages.filter((m) => !m.read).length + data.inquiries.filter((m) => !m.read).length > 0 && (
                <span className="ml-2 text-gold">●</span>
              )}
            </button>
          ))}
        </nav>

        <main className="flex-1 p-6 lg:p-10 max-w-5xl">
          {tab === "Projects" && <ProjectsPanel projects={data.projects} reload={reload} />}
          {tab === "Documents" && <DocumentsPanel documents={data.documents} reload={reload} />}
          {tab === "Requests" && <RequestsPanel requests={data.accessRequests} reload={reload} />}
          {tab === "Updates" && <UpdatesPanel updates={data.updates} reload={reload} />}
          {tab === "Inbox" && <InboxPanel messages={data.messages} inquiries={data.inquiries} accessRequests={data.accessRequests} reload={reload} />}
          {tab === "Settings" && <SettingsPanel settings={data.settings} reload={reload} />}
        </main>
      </div>
    </div>
  );
}
